// V2 版式：pyramid（前端 DOM 版）——对照后端 v2/layouts/pyramid.ts 逐坐标移植
// 居左 5.5in 等腰梯形堆叠（顶层 30%→底层 100%）；顶层 accent；右侧注释带 hairline 引导线。

import { SPACING } from "../../lib/tokens.generated";
import { V2GEO } from "../../lib/v2geo.generated";
import { px, pt, titleSize, kickerOf, footOf } from "./shared";
import { luxeExt } from "./luxe";
import { reportTheme, inkTheme, memphisTheme } from "./themes";
import V2Chrome from "./Chrome";
import { EditableText } from "../SlideCanvas";
import type { V2LayoutProps } from "./V2Slide";

export default function V2Pyramid({ c, skin, page, total, editable, handlers }: V2LayoutProps) {
  const GEO = V2GEO["pyramid"] as any;
  const m = SPACING.marginX;
  const W = 13.333;
  const mag = skin.style === "magazine";
  // 层底色：瑞士 grey1→grey2 灰阶 / luxe 浅金 blush→accentLight 裸金渐层（pyramid.ts 分支）
  const ext = luxeExt(skin);
  const theme = skin.theme as { grey?: { grey1?: string; grey2?: string } };
  const grey1 = theme.grey?.grey1 ?? (ext ? ext.blush : skin.hairline);
  const grey2 = theme.grey?.grey2 ?? (ext ? ext.accentLight : skin.sub);
  // 第二批皮肤（043 §4.5–4.7）：report 顶层纯黑/ink 顶层墨黑+朱红细边/memphis 顶层橙满铺其余白底
  const reportT = reportTheme(skin);
  const inkT = inkTheme(skin);
  const mpT = memphisTheme(skin);

  const bi = c.blocks.findIndex((x) => x.type === "pyramid");
  const b = bi >= 0 ? c.blocks[bi] : null;
  if (!b || b.type !== "pyramid") {
    return <V2Chrome skin={skin} kicker={kickerOf(page, "pyramid")} page={page} total={total} foot={footOf()} />;
  }

  const n = b.layers.length;
  const widthRatio = (i: number) => n <= 1 ? 1 : GEO.topWidthRatio + (1 - GEO.topWidthRatio) * (i / (n - 1));
  const noteX = GEO.pyramidX + GEO.pyramidW + GEO.noteGap;
  const noteW = W - m - noteX;

  return (
    <>
      <V2Chrome skin={skin} kicker={kickerOf(page, "pyramid")} page={page} total={total} foot={footOf()} />
      <EditableText
        value={c.title} path="title" editable={editable} multiline
        onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
        style={{
          position: "absolute", left: px(m), top: px(GEO.titleY), width: px(W - 2 * m), height: px(GEO.titleH),
          fontSize: pt(titleSize(c.title, skin.scale.pageTitle)), color: skin.text,
          fontFamily: skin.fontHead, fontWeight: mag ? 700 : 400, lineHeight: 1.1,
        }}
      />
      {b.layers.map((layer, i) => {
        const y = GEO.topY + i * (GEO.layerH + GEO.layerGap);
        const w = GEO.pyramidW * widthRatio(i);
        const x = GEO.pyramidX + (GEO.pyramidW - w) / 2;
        const top = i === 0;
        // 层底色：memphis 顶层橙满铺其余白底；report 顶层纯黑+其余深灰；ink 顶层墨黑+其余黛灰（pyramid.ts:68-77）
        const fill = mpT
          ? (top ? mpT.accent : "#FFFFFF")
          : reportT ? (top ? reportT.inkDeep : reportT.muted)
          : inkT ? (top ? inkT.ink : inkT.muted)
          : top ? skin.accent : n <= 3 || i < n - 1 ? grey1 : grey2;
        const cy = y + GEO.layerH / 2;
        return (
          <div key={i}>
            <div style={{ position: "absolute", left: px(x), top: px(y), width: px(w), height: px(GEO.layerH), background: fill, border: inkT && top ? `1px solid ${inkT.accent}` : undefined }} />
            <EditableText
              value={layer.title} path={`blocks.${bi}.layers.${i}.title`} editable={editable}
              onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
              style={{
                position: "absolute", left: px(x + 0.15), top: px(y), width: px(w - 0.3), height: px(GEO.layerH),
                fontSize: pt(skin.scale.sectionHead), color: top ? (reportT ? reportT.ink : inkT ? inkT.paper : skin.accentOn) : skin.text,
                fontFamily: skin.fontBody, fontWeight: 700, textAlign: "center",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            />
            {/* hairline 引导线（层中心 → 注释区） */}
            <div style={{
              position: "absolute", left: px(GEO.pyramidX + GEO.pyramidW + 0.04), top: px(cy),
              width: px(GEO.noteGap - 0.08), height: 1, background: skin.sub,
            }} />
            <EditableText
              value={layer.desc} path={`blocks.${bi}.layers.${i}.desc`} editable={editable} multiline
              onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
              style={{
                position: "absolute", left: px(noteX), top: px(cy - GEO.noteH / 2), width: px(noteW), height: px(GEO.noteH),
                fontSize: pt(skin.scale.body), color: skin.text, fontFamily: skin.fontBody,
                lineHeight: 1.2, display: "flex", alignItems: "center",
              }}
            />
          </div>
        );
      })}
    </>
  );
}
